import React, { FC, useCallback } from 'react'
import { usePartialSubscription } from '../../../../stateData/partialSubscription'
import { useSettings } from '../../../../stateData/settings'
import { isStateEmpty } from '../../../../lib/stateHelpers'
import { isMobileOs, isDesktopOs } from '../../../../lib/osHelpers'
import { LoadingPage } from '../../../shared/LoadingPage'
import { Button } from '../../../shared/Button'
import {
  useAnalytics,
  useExitFlow,
  useGetText,
  useSignupSession,
  useUserLocale,
  useOnboardingOsType
} from '../../../../hooks'
import { openChatbot } from './chatbot'
import { MfeComponent } from '../../../../infrastructure/MfeComponent'
import { HpxCard } from '../HpxCard'

type ManualPrinterClaimProps = {
  mpcBasePath: string
  claimCallback: (params: {
    code: string
    confirm_transfer: boolean
  }) => Promise<void>
}

export const ManualPrinterClaim: FC<ManualPrinterClaimProps> = ({
  mpcBasePath,
  claimCallback
}) => {
  const partialSubscriptionState = usePartialSubscription()
  const settingsState = useSettings()
  const exitOobe = useExitFlow()
  const getText = useGetText()
  const { publishCustomEvent } = useAnalytics()
  const { isHpx } = useSignupSession()
  const { country, language } = useUserLocale()
  const osType = useOnboardingOsType()

  const onClaimClick = useCallback(
    // isPrinterGifting needs manual verfication
    async (claimCode: string, isPrinterGifting = false) => {
      await claimCallback({
        code: claimCode,
        confirm_transfer: isPrinterGifting
      })
    },
    [claimCallback]
  )

  const APIStates = [partialSubscriptionState, settingsState]
  if (isStateEmpty(APIStates)) {
    return <LoadingPage />
  }

  const {
    instructionSet,
    enableJarvisMpcLegacyClaimStep
  } = partialSubscriptionState.data
  const { showSupportPhone, enablePresalesChatbot } = settingsState.data

  const flowNameProp = () => {
    if (isMobileOs(osType)) {
      return 'moobe'
    }
    if (isDesktopOs(osType)) {
      return 'woobe'
    }
    return 'oobe'
  }

  const onError = (message: string) => {
    publishCustomEvent({
      event: 'e_formError',
      formName: 'claimConfirmation',
      formError: message
    })
  }

  const skipButton = (
    <Button
      data-analyticsid="SkipOfferLink"
      appearance="ghost"
      onClick={() => exitOobe('declined', {})}
    >
      {getText('connect_step.buttons.skip_instant_ink')}
    </Button>
  )

  return (
    <HpxCard>
      <MfeComponent
        name="react-manual-printer-claim"
        props={{
          onClaimClick: onClaimClick,
          onJarvisError: onError,
          flowName: flowNameProp(),
          instructionSet: instructionSet,
          language: language,
          country: country,
          virtual_agent_support: enablePresalesChatbot
            ? openChatbot
            : undefined,
          call_support: showSupportPhone
            ? getText('navbar.navigation_links.support_phone')
            : undefined,
          base_path: mpcBasePath,
          trackClickEvent: () => null,
          skipButton: skipButton,
          enableLegacy: enableJarvisMpcLegacyClaimStep,
          isHpx: isHpx
        }}
      />
    </HpxCard>
  )
}
